import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import { AddAddressButton } from "@pages/AdminDashboard/AddressManager/AddAddressButton";
import { AddressFields } from "@pages/AdminDashboard/AddressManager/AddressFields";
import {
  emptyAddressFields,
  isAddressComplete,
  toAddressFields,
} from "@pages/AdminDashboard/AddressManager/addressFormValues";
import { useIsPhoneLayout } from "@pages/AdminDashboard/theme/AdminLayout";
import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { Banner } from "@pages/AdminDashboard/ui/Banner";
import { Button } from "@pages/AdminDashboard/ui/Button";
import { ConfirmDialog } from "@pages/AdminDashboard/ui/ConfirmDialog";
import { RadioCardGroup } from "@pages/AdminDashboard/ui/RadioCardGroup";
import { VisuallyHidden } from "@pages/AdminDashboard/ui/DataTable/visuallyHidden";
import { changeShipAddress, saveShipAddress } from "@store/admin/adminAction";
import { shipAddressDialogClosed } from "@store/admin/adminSlice";
import { ShipAddress, ShipAddressFields, ShipAddressState, ShipCase } from "@types-runway/admin";
import { Order } from "@types-runway/case";
import { RootState } from "@types-runway/state";

import { nameCase } from "./caseName";
import { describeShipAddress } from "./shipAddressFormat";

const { color, typeScale } = adminTokens;
const [, spaceSm, , spaceMd] = adminTokens.spacing;
const radioName = "ship-address";

const Rows = styled.div<{ $isPhone: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ $isPhone }) => ($isPhone ? spaceMd : spaceSm)}px;
`;

// On a phone Edit stands below its address card, as wide as the card and nearer to it than to
// the next address (SHIPUI-R42…R44).
const Row = styled.div<{ $isPhone: boolean }>`
  display: flex;
  flex-direction: ${({ $isPhone }) => ($isPhone ? "column" : "row")};
  align-items: ${({ $isPhone }) => ($isPhone ? "stretch" : "center")};
  gap: ${spaceSm}px;

  // The global label margin would push Edit away from its own card, toward the next one.
  ${({ $isPhone }) => $isPhone && "> fieldset label { margin-bottom: 0; }"}

  > fieldset {
    flex: 1;
    min-width: 0;
  }

  > fieldset > div {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Sequence = styled.ul`
  display: flex;
  flex-direction: column;
  gap: ${spaceSm}px;
  margin: 0;
  padding: 0;
  list-style: none;
  color: ${color.inkSoft};
  font-size: ${typeScale.meta.fontSize};
`;

const FormTitle = styled.h3`
  margin: 0;
  font-size: ${typeScale.labelValue.fontSize};
  font-family: ${typeScale.labelValue.fontFamily};
  font-weight: normal;
`;

interface ShipAddressDialogProps {
  shipCase: ShipCase;
  activeOrder?: Order;
  hasShipped: boolean;
}

type DialogMode = "list" | "form";

// The dialog that changes where a Case ships (SHIPUI-R23…R31): choose among the User's saved
// addresses, fix one in place or add one, then persist it on the Case before the Order is
// re-sent (SHIP-R2).
export const ShipAddressDialog = ({
  shipCase,
  activeOrder,
  hasShipped,
}: ShipAddressDialogProps) => {
  const dispatch = useDispatch();
  const isPhone = useIsPhoneLayout();
  const { dialog, addresses, step, failure } = useSelector<RootState, ShipAddressState>(
    (state) => state.admin.shipAddress
  );
  const currentId = shipCase.shippingAddress?.id ?? "";
  const userId = shipCase.UserId as string;

  const [mode, setMode] = useState<DialogMode>("list");
  const [chosenId, setChosenId] = useState(currentId);
  const [fields, setFields] = useState<ShipAddressFields>(emptyAddressFields);
  const [isSavingForm, setIsSavingForm] = useState(false);
  // Whether the failure on show came from saving the form rather than from changing the address.
  const [failedInForm, setFailedInForm] = useState(false);

  // The addresses listed before a form was saved, to spot the one it adds.
  const knownIds = useRef(new Set<string>());
  useEffect(() => {
    if (!isSavingForm) knownIds.current = new Set(addresses.map((address) => address.id));
  }, [addresses, isSavingForm]);

  useEffect(() => {
    if (dialog !== "loading") return;

    setMode("list");
    setChosenId(currentId);
    setIsSavingForm(false);
  }, [dialog, currentId]);

  // A saved address joins the list and is chosen, then the form gives way to the list (SHIPUI-R30).
  useEffect(() => {
    if (!isSavingForm) return;

    const stored = addresses.find((address) =>
      fields.id ? address.id === fields.id : !knownIds.current.has(address.id)
    );
    if (!stored) return;

    setChosenId(stored.id);
    setMode("list");
    setIsSavingForm(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addresses]);

  useEffect(() => {
    if (!failure) return;

    setFailedInForm(isSavingForm);
    setIsSavingForm(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [failure]);

  const isOpen = dialog !== "closed";
  const isWorking = dialog === "working";
  const chosen = addresses.find((address) => address.id === chosenId);
  const orderId = activeOrder?.order_id;

  const close = () => dispatch(shipAddressDialogClosed());

  const openForm = (address?: ShipAddress) => {
    setFields(address ? toAddressFields(address) : emptyAddressFields);
    setMode("form");
  };

  const saveForm = () => {
    setIsSavingForm(true);
    dispatch(saveShipAddress({ userId, fields }));
  };

  const confirmChoice = () => {
    if (!chosen) return;

    dispatch(
      changeShipAddress({
        caseNumber: shipCase.id as number,
        address: chosen,
        activeOrder: activeOrder ? { id: activeOrder.id, order_id: activeOrder.order_id } : null,
        hasShipped,
      })
    );
  };

  const dialogProps = {
    isOpen,
    eyebrow: "Change shipping address",
    title: `Where should ${nameCase(shipCase)} ship?`,
  };

  if (!isOpen) return null;

  if (dialog === "loadFailed") {
    return (
      <ConfirmDialog
        {...dialogProps}
        dismissLabel="Keep current address"
        confirmLabel="Use this address"
        pendingLabel="Use this address"
        isConfirmDisabled
        onDismiss={close}
        onConfirm={close}
      >
        <Banner
          tone="error"
          title="The User's addresses couldn't be loaded."
          body="Nothing changed."
        />
      </ConfirmDialog>
    );
  }

  if (mode === "form" && !isWorking) {
    return (
      <ConfirmDialog
        {...dialogProps}
        dismissLabel="Back"
        confirmLabel="Save address"
        pendingLabel="Save address"
        isPending={isSavingForm}
        isConfirmDisabled={!isAddressComplete(fields)}
        onDismiss={() => setMode("list")}
        onConfirm={saveForm}
      >
        <FormTitle>{fields.id ? "Edit address" : "Add a new address"}</FormTitle>
        {failure && failedInForm && (
          <Banner
            tone="error"
            title="The address wasn't saved."
            body={`Request failed (${failure}). Nothing changed.`}
          />
        )}
        <AddressFields fields={fields} onChange={setFields} />
      </ConfirmDialog>
    );
  }

  return (
    <ConfirmDialog
      {...dialogProps}
      dismissLabel="Keep current address"
      confirmLabel="Use this address"
      pendingLabel="Use this address"
      isPending={isWorking}
      isConfirmDisabled={dialog !== "choosing" || !chosen || chosenId === currentId}
      onDismiss={close}
      onConfirm={confirmChoice}
    >
      {failure && !failedInForm && (
        <Banner
          tone="error"
          title="The address wasn't changed."
          body={`Request failed (${failure}). The Case still ships to its current address.`}
        />
      )}
      <Rows $isPhone={isPhone}>
        {addresses.map((address) => (
          <Row key={address.id} $isPhone={isPhone}>
            <RadioCardGroup
              legend={describeShipAddress(address)}
              isLegendHidden
              name={radioName}
              options={[
                {
                  value: address.id,
                  label: describeShipAddress(address),
                  tag: address.id === currentId ? "On this Case" : undefined,
                  isDisabled: isWorking,
                },
              ]}
              value={chosenId}
              onChange={setChosenId}
            />
            <Button look="secondary" disabled={isWorking} onClick={() => openForm(address)}>
              Edit<VisuallyHidden> {describeShipAddress(address)}</VisuallyHidden>
            </Button>
          </Row>
        ))}
        <AddAddressButton isDisabled={isWorking} onClick={() => openForm()} />
      </Rows>
      {isWorking && (
        <Sequence aria-live="polite">
          {step === "resending" && <li>Address saved to the Case ✓</li>}
          {step === "resending" && orderId && <li>Re-sending OID {orderId} to the pharmacy…</li>}
        </Sequence>
      )}
    </ConfirmDialog>
  );
};
